import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useFloatingActionPanel } from './components/floatingPanel/useFloatingActionPanel';
import { FloatingActionPanel } from './components/floatingPanel/FloatingActionPanel';
import './styles/ReviewDocuments.css';
import { db, auth } from '../../../firebase';
import { TriggerRenderDocTemplate } from '../../../utils/TriggerRenderDocTemplate';
import { InitialWarningDialog } from './components/InitialWarningDialog';
import { doc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { TriggerCoverPage } from '../../../utils/TriggerCoverPage';
import { triggerPadCoverPageIfNeeded } from '../../../utils/padCoverPageTrigger';
import WarningBar from './components/WarningBar';
import DocumentReviewHeader from './components/DocumentReviewHeader';
import { renderModelInput as _renderModelInput } from '../../../utils/renderModelInput';
import { TriggerPADCompletionEmail } from './functions/TriggerPADCompletionEmail';
import { decodeHtmlEntities } from './functions/htmlEncoders';
import { handlePlainTextPaste, suppressFormattingShortcuts } from './functions/contentEditableUtils';
import { checkReportLogicVersion } from '../../../utils/featureFlagUtils';
import { rerenderPatientReportIfStale } from '../../../utils/autoPatientReport';
import { rerenderCoverPageIfStale } from '../../../utils/autoCoverPage';
import { appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { usePendingApprovals } from '../../../hooks/usePendingApprovals';
import { finalizeSectionReview } from '../../../utils/sectionReviewFinalization';
import { computeUnpersistedApprovals } from '../../../utils/approvalStateUtils';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { readIsCarryOutHealthChecksNow } from '../../../utils/padNavigationUtils';
import { useApprovalTracking } from '../../../hooks/useApprovalTracking';
import { toast } from 'react-toastify';
import {
  emitParagraphApproval,
  emitParagraphEdited,
  emitParagraphReset,
} from '../../../analytics/reportEvents';
import { useAnthropicComparison } from '../../../hooks/useAnthropicComparison';
import { CompareModeToolbar } from './components/CompareModeToolbar';
import { CompareClaudePanel } from './components/CompareClaudePanel';

export default function ReviewDocumentPAD() {

  const { patientId } = useParams();
  const { claims } = useAuth();

  const MODULE_NUMBER = 4
  const MODULE_PREFIX_CODE = "pad"
  const MODULE_TITLE = "Post-Assessment Discussion"

  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, clearPatientData } = useContext(PatientDataContext);

  // Used to skip standalone Cover Page generation for TOC / Phase 2 SLaM
  // Report Builder workflows — see replaceCARwithReportBuilder.
  const wf = useWorkflowConfig(patientDataModel, claims);

  // State for feature flag
  const [reportLogicVersion, setReportLogicVersion] = useState(1);
  const [isLoadingFeatureFlag, setIsLoadingFeatureFlag] = useState(true);

  useEffect(() => {
    // Force fresh fetch
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  // Check feature flag when component mounts
  useEffect(() => {
    const checkFeatureFlag = async () => {
      if (!patientId) return;
      
      try {
        setIsLoadingFeatureFlag(true);
        const version = await checkReportLogicVersion(patientId);
        setReportLogicVersion(version);
      } catch (error) {
        console.error('Error checking feature flag:', error);
        setReportLogicVersion(1); // Default to old logic
      } finally {
        setIsLoadingFeatureFlag(false);
      }
    };

    checkFeatureFlag();
  }, [patientId]);

  // Legacy document structure (version 1)
  const legacyDocStructure = useMemo(() => ({
    "Review of Assessment": {
      "1": { "title": "Summary of Assessment Findings", "code": "pad_REV_summary_of_findings" },
      "2": { "title": "Patient's Goals", "code": "pad_REV_patient_goals" },     
    },
    "Strategies and Recommendations": {
      "1": { "title": "Summary of Discussed Topics", "code": "pad_DIS_summary" },
    },
    "Treatment Options": {
      "1": { "title": "Treatment Options Discussed", "code": "pad_TRT_options_discussed" },
      "2": { "title": "Patient's Treatment Preferences", "code": "pad_TRT_patient_preferences" },
    },
    "Health Checks": {
      "1": { "title": "Weight", "code": "pad_HLT_weight" },
      "2": { "title": "Blood Pressure", "code": "pad_HLT_blood_pressure" },
      "3": { "title": "Pulse", "code": "pad_HLT_pulse" },
      "4": { "title": "Medical History Review", "code": "pad_HLT_medical_history_review" },
      "5": { "title": "ADHD / Controlled Drug Prescribing", "code": "pad_HLT_adhd_controlled_drug_prescribing" },
      "6": { "title": "Outcome of Health Checks", "code": "pad_HLT_outcome_of_health_checks" },
    },
    "Agreed Medication Plan": {
      "1": { "title": "Medication Plan", "code": "pad_AMP_agreed_medication_plan" },
      "2": { "title": "Weekly Instructions", "code": "pad_AMP_titration_schedule" },
    },
  }), []);

  // Unified document structure (version 2)
  const unifiedDocStructure = useMemo(() => ({
    "Review of Assessment": {
      "1": { "title": "Summary of Assessment Findings", "code": "pad_REV_summary_of_findings" },
      "2": { "title": "Patient's Goals", "code": "pad_REV_patient_goals" },
      "3": { "title": "Workplace Needs Assessment", "code": "pad_not_include_wna" },
    },
    "Strategies and Recommendations": {
      "1": { "title": "Summary of Discussed Topics", "code": "pad_DIS_summary" },
      "2": { "title": "Driving", "code": "pl_driving" },
      "3": { "title": "Physical Activity", "code": "pl_lifestylePhysicalActivity" },
      "4": { "title": "Diet", "code": "pl_lifestyleDiet" },
      "5": { "title": "Sleep", "code": "pl_lifestyleSleep" },
      "6": { "title": "Alcohol", "code": "pl_lifestyleAlcohol" },
      "7": { "title": "Smoking & Tobacco Use", "code": "pl_lifestyleSmoking" },
      "8": { "title": "Caffeine", "code": "pl_lifestyleCaffeine" },
      "9": { "title": "Gambling & Gaming Behaviour", "code": "pl_lifestyleGambling" },
      "10": { "title": "Substance Use", "code": "pl_lifestyleSubstanceUse" },
      "11": { "title": "Financial Management", "code": "pl_lifestyleFinances" },
      "12": { "title": "Environmental Modifications at Home", "code": "pl_environmentHome" },
      "13": { "title": "Environmental Modifications at Work", "code": "pl_environmentWork" },
      "14": { "title": "Environmental Modifications in Education", "code": "pl_environmentEducation" },
    },
    "Treatment Options": {
      "1": { "title": "Treatment Options Discussed", "code": "pad_TRT_options_discussed" },
      "2": { "title": "Patient's Treatment Preferences", "code": "pad_TRT_patient_preferences" },
    },
    "Health Checks": {
      "1": { "title": "Weight", "code": "pad_HLT_weight" },
      "2": { "title": "Blood Pressure", "code": "pad_HLT_blood_pressure" },
      "3": { "title": "Pulse", "code": "pad_HLT_pulse" },
      "4": { "title": "Medical History Review", "code": "pad_HLT_medical_history_review" },
      "5": { "title": "ADHD / Controlled Drug Prescribing", "code": "pad_HLT_adhd_controlled_drug_prescribing" },
      "6": { "title": "Outcome of Health Checks", "code": "pad_HLT_outcome_of_health_checks" },
    },
    "Agreed Medication Plan": {
      "1": { "title": "Medication Plan", "code": "pad_AMP_agreed_medication_plan" },
      "2": { "title": "Week 1 Instructions", "code": "pl_medicationPlanWeek1" },
      "3": { "title": "Week 2 Instructions", "code": "pl_medicationPlanWeek2" },
      "4": { "title": "Week 3 Instructions", "code": "pl_medicationPlanWeek3" },
      "5": { "title": "Week 4 Instructions", "code": "pl_medicationPlanWeek4" },
    },
  }), []);

  // Choose the appropriate structure based on feature flag.
  // When the clinician deferred Health Checks ("No - defer"), drop Health Checks
  // and Agreed Medication Plan from the review structure entirely — even if stale
  // pad_HLT_* / pad_AMP_* outputs somehow remain. Content-empty sections already
  // hide themselves; this is belt-and-braces so a Yes→visit→No-defer path cannot
  // surface those paragraphs in the review UI.
  const isCarryOutHealthChecksNow = readIsCarryOutHealthChecksNow(patientDataModel);
  const docStructure = useMemo(() => {
    const base = isLoadingFeatureFlag
      ? legacyDocStructure
      : (reportLogicVersion === 2 ? unifiedDocStructure : legacyDocStructure);

    if (isCarryOutHealthChecksNow) return base;

    const { ['Health Checks']: _hlt, ['Agreed Medication Plan']: _amp, ...rest } = base;
    return rest;
  }, [
    reportLogicVersion,
    isLoadingFeatureFlag,
    legacyDocStructure,
    unifiedDocStructure,
    isCarryOutHealthChecksNow,
  ]);

  const REPORT_DATA_OUTPUTS = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.outputs || {},
    [patientDataModel]);

  // The paragraph edit history, read from the SAME already-loaded section as the
  // outputs above. It is what lets the adoption numbers (how much of the AI draft
  // survived) be computed at the moment of an edit rather than by reading the
  // report back out of Firestore later - and because this object is already in
  // memory to render the page, it costs no additional read.
  //
  // In-memory means it can lag a write made elsewhere in the same instant. That
  // only matters for a paragraph's FIRST edit, and only if another session edited
  // it seconds earlier; once the earliest entry exists it stays the earliest
  // forever. The nightly reconciliation reads authoritative Firestore, so a
  // disagreement surfaces there rather than going unnoticed.
  const REPORT_EDIT_HISTORY = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.editHistory || {},
    [patientDataModel]);

  const [state, setState] = useState({
    selectedCode: null,
    textareaContent: null,
    isSidePanelOpen: false,
    modelInputsFirestore: null,
    clinicianInputs: null,
    clinicianDecisions: null,
    checkedExamples: null,
    article: null,
    // patientInputs: null,
    // informantInputs: null
  });

  // Lifestyle / environment / driving paragraphs store the Think Divergent
  // evidence-based article that was sent to the model under the Article
  // bucket. Other PAD paragraphs leave that bucket empty. articleLinks
  // turns an http(s) source into a "Link to article" (same as RD_PR); a
  // non-URL source such as "Reference article: Alcohol" still renders as
  // plain "in {source}" text.
  const renderModelInput = (role, inputs) =>
    _renderModelInput(role, inputs, { articleLinks: true });

  // Shared read so click / mark-as-done / skip all populate the same
  // buckets. Forgetting Article on one of those paths is how a used
  // article would vanish after Skip.
  const readParagraphInputs = (inputs, code) => {
    const paragraph = (inputs && inputs[code]) || {};
    return {
      clinicianInputs: paragraph['Clinician Notes'],
      clinicianDecisions: paragraph['Clinician Decisions'],
      checkedExamples: paragraph['Checklist Examples Ticked'],
      patientInputs: paragraph['Patient Prefill'],
      article: paragraph['Article'],
    };
  };

  const hasArticleInput = Object.values(state.article || {}).some(
    (input) => input && String(input.value || '').trim()
  );

  const [isEditing, setIsEditing] = useState(false);

  const {
    totalParagraphs, reviewedCodes, isFullyApproved,
    showWarning, addReviewedCode, removeReviewedCode,
  } = useApprovalTracking({
    patientId,
    moduleNumber: MODULE_NUMBER,
    docStructure,
    reportDataOutputs: REPORT_DATA_OUTPUTS,
  });

  // Tracks in-flight per-paragraph approval writes so Save & Exit can wait for
  // them to commit before deriving and persisting the section approval flag.
  const { track: trackApproval, settleAll: settlePendingApprovals } = usePendingApprovals();

  // ── Anthropic comparison (dev/staging UAT only) ──────────────────────────
  const padSections = useMemo(() => Object.keys(docStructure), [docStructure]);
  const {
    isAvailable: isCompareAvailable,
    isLoading: isCompareLoading,
    hasComparison,
    showClaude,
    sideBySide,
    claudeOutputs,
    progress: compareProgress,
    triggerComparison,
    setViewMode,
    toggleSideBySide,
    getDisplayedOutput,
  } = useAnthropicComparison({
    patientId,
    reportStage: MODULE_TITLE,
    reportSections: padSections,
    conditions: [],
    orgId: patientDataModel?.orgId,
    organisationName: claims?.organisation,
  });

  const handleParagraphClick = (event, code, content) => {
    // Reset edit states
    setIsEditing(false);

    if (!state.modelInputsFirestore) {
      console.error('modelInputsFirestore is not populated');
      return;
    }

    if (code && state.modelInputsFirestore[code]) {
      setState(prevState => ({
        ...prevState,
        selectedCode: code,
        textareaContent: content || ' ', // always having at least a non-breaking space
        ...readParagraphInputs(state.modelInputsFirestore, code),
        // informantInputs: state.modelInputsFirestore[code]["Informant Prefill"],
        isSidePanelOpen: true
      }));
      setTimeout(() => safeUpdatePosition(code), 0);
    } else {
      console.error('No matching input found for the code:', code);
    }
  };

  // Update the Section component to only render paragraphs with content
  const Section = ({ title, paragraphs }) => {
    // First filter out paragraphs that don't have content in REPORT_DATA_OUTPUTS
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([_, { code }]) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ""
    );

    // Only render the section if it has at least one available paragraph
    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4 text-blue-500">{title}</h2>

        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = getDisplayedOutput(code, REPORT_DATA_OUTPUTS[code]);
          const decodedContent = decodeHtmlEntities(content); // Decode for display        
          return (
            <div key={key} className="mb-4">
              {subTitle && (
                <h3 className="font-semibold mb-2 text-gray-900">
                  {subTitle}
                </h3>
              )}
              <div
                data-paragraph-container
                className={`
                relative text-sm font-medium p-2 rounded-lg
                transition-all duration-200
                ${isEditing && state.selectedCode === code
                    ? 'bg-amber-100 z-20' // Edit mode background
                    : reviewedCodes.has(code)
                      ? 'bg-green-600/30 hover:bg-green-600/40 approved-paragraph'
                      : 'bg-violet-100 hover:bg-violet-200'
                  }
                ${state.selectedCode === code
                    ? 'ring ring-violet-800'
                    : ''
                  }
                ${!isEditing ? 'cursor-pointer' : ''}
              `}
                id={code}
                onClick={(e) => {
                  if (!isEditing) {
                    handleParagraphClick(e, code, decodedContent);
                  }
                }}
              >
                <p
                  ref={editableContentRef}
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  contentEditable={isEditing && state.selectedCode === code && !showClaude}
                  onKeyDown={suppressFormattingShortcuts}
                  onInput={(e) => {
                    if (isEditing && state.selectedCode === code) {
                      let newContent = e.target.innerText;
                      newContent = newContent.replace(/\n/g, '\n');

                      if (REPORT_DATA_OUTPUTS) {
                        REPORT_DATA_OUTPUTS[code] = newContent;
                      }
                      safeUpdatePosition(code);
                    }
                  }}
                  onPaste={handlePlainTextPaste}
                  suppressContentEditableWarning={true}
                >
                  {decodedContent}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };


  // Add a ref for the paragraph content
  const editableContentRef = useRef(null);

  // Update handleStartEdit to focus the content
  const handleStartEdit = () => {
    if (state.selectedCode) {
      // Store the current content before starting edit
      setPreEditContent(prev => ({
        ...prev,
        [state.selectedCode]: REPORT_DATA_OUTPUTS[state.selectedCode] || ''
      }));
    }
    setIsEditing(true);
    // Use setTimeout to ensure the contentEditable is enabled before focusing
    setTimeout(() => {
      const paragraphElement = document.querySelector(`#${state.selectedCode} p`);
      if (paragraphElement) {
        paragraphElement.focus();
        // Create a range and selection to place cursor at the end
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(paragraphElement);
        range.collapse(false); // false means collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
      }
      safeUpdatePosition(state.selectedCode);
    }, 0);
  };

  const handleSaveEdit = () => {
    if (state.selectedCode) {
      // Get the current and previous values
      const spanCode = state.selectedCode;
      const newValue = REPORT_DATA_OUTPUTS[spanCode];
      const previousValue = initialContent[spanCode] || '';

      // Only proceed if content actually changed
      if (newValue !== previousValue) {
        // Optimistically update UI first
        removeReviewedCode(state.selectedCode);
        setIsEditing(false);
        safeUpdatePosition(state.selectedCode);


        // Create history entry
        const editEntry = {
          clinicianId: auth.currentUser.uid,
          clinicianName: auth.currentUser.displayName,
          timestamp: serverTimestamp(),
          previousValue,
          newValue,          
          editType: "manual",
        };
        const timestampKey = Date.now().toString();

        // Base path to this section. MODULE_NUMBER and timestampKey are numeric
        // segments; Firestore's SDK backtick-escapes digit-leading segments
        // automatically when it builds the update mask, so the dotted-string key
        // form is safe here (and matches handleParagraphApproval exactly).
        const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;

        // Write ONLY the affected keys with a plain updateDoc rather than a
        // read-modify-write runTransaction. Transactions commit with a
        // document-version precondition; on slow 3G/4G the single patient doc is
        // being touched concurrently (in-flight approvals, auto report/cover
        // re-renders, lastReviewedAt bumps) so the transaction exhausts its
        // retries and throws "the stored version does not match the required
        // base version" — which silently discarded the clinician's edit. A
        // targeted updateDoc has no version precondition and Firestore merges
        // writes to distinct field paths server-side. See handleParagraphApproval
        // for the same reasoning applied to approvals.
        const updateData = {
          // Persist the edited text (targeted, replacing the whole-map rewrite)
          [`${sectionPath}.outputs.${spanCode}`]: newValue,
          // Un-approve this paragraph: editing invalidates a prior approval
          [`${sectionPath}.approvedParagraphs.${spanCode}`]: deleteField(),
          // Append an audit entry under this paragraph's edit history
          [`${sectionPath}.editHistory.${spanCode}.${timestampKey}`]: editEntry,
          // Review metadata (mirrors the approval write)
          [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
          [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
          [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
        };

        // Perform Firestore update in the background
        const startedAt = performance.now();
        updateDoc(doc(db, 'patients', patientId), updateData).then(() => {
          // Confirms the edit persisted AND the paragraph was un-approved
          // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
          const elapsedMs = Math.round(performance.now() - startedAt);
          console.log(`[edit] saved & UNAPPROVED "${spanCode}" (content updated) in ${elapsedMs}ms`);
          // Analytics mirror of the edit that just persisted: paragraph code,
          // section and the two lengths only — never the wording itself.
          emitParagraphEdited({
            patientId,
            module: MODULE_PREFIX_CODE,
            docStructure,
            code: spanCode,
            prevText: previousValue,
            priorHistory: REPORT_EDIT_HISTORY[spanCode] || {},
            nextText: newValue,
          });
        }).catch(error => {
          console.error("Error updating paragraph approval on edit:", error);
          // Roll back optimistic update on error and surface the failure
          addReviewedCode(state.selectedCode);
          toast.error(
            'Your edit did not save. Please try editing the paragraph again.',
            { toastId: 'paragraph-edit-save-error' }
          );
        });
      // Mark section as dirty since a change was saved
      setSectionDirty(true);
      } else {
        // If no changes, just exit edit mode
        setIsEditing(false);
        safeUpdatePosition(state.selectedCode);
      }
    }
  };

  // Update handleCancel to revert to pre-edit content
  const handleCancel = () => {
    if (state.selectedCode && preEditContent[state.selectedCode]) {
      // Revert to the content before this edit session started
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = preEditContent[state.selectedCode];
      }
    }
    setIsEditing(false);
    safeUpdatePosition(state.selectedCode);
  };

  const handleReset = () => {
    if (state.selectedCode && initialContent[state.selectedCode]) {
      const spanCode = state.selectedCode;
      const newValue = initialContent[state.selectedCode];
      const previousValue = REPORT_DATA_OUTPUTS[state.selectedCode];

      // Optimistically update UI
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = initialContent[state.selectedCode];
      }

      // Remove from reviewed codes optimistically
      removeReviewedCode(state.selectedCode);

      // Create history entry for the reset
      const editEntry = {
        clinicianId: auth.currentUser.uid,
        clinicianName: auth.currentUser.displayName,
        timestamp: serverTimestamp(),
        previousValue,
        newValue,
        isReset: true  // Flag to indicate this was a reset action
      };
      const timestampKey = Date.now().toString();

      // Same targeted-write approach as handleSaveEdit / handleParagraphApproval:
      // a plain updateDoc on specific field paths (no version precondition)
      // instead of a read-modify-write runTransaction, which contends on the
      // single patient doc and fails under slow 3G/4G (losing the reset).
      const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;
      const updateData = {
        // Restore the original generated text
        [`${sectionPath}.outputs.${spanCode}`]: newValue,
        // A reset also invalidates any prior approval
        [`${sectionPath}.approvedParagraphs.${spanCode}`]: deleteField(),
        // Record the reset in this paragraph's edit history
        [`${sectionPath}.editHistory.${spanCode}.${timestampKey}`]: editEntry,
        // Review metadata (mirrors the approval write)
        [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
        [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
        [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
      };

      // Perform Firestore update
      const startedAt = performance.now();
      updateDoc(doc(db, 'patients', patientId), updateData).then(() => {
        // Confirms the reset persisted AND the paragraph was un-approved
        // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
        const elapsedMs = Math.round(performance.now() - startedAt);
        console.log(`[reset] reset & UNAPPROVED "${spanCode}" in ${elapsedMs}ms`);
        // Analytics mirror of the reset that just persisted (code only).
        emitParagraphReset({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code: spanCode,
        });
      }).catch(error => {
        console.error("Error resetting paragraph:", error);
        // Roll back optimistic updates on error and surface the failure
        if (REPORT_DATA_OUTPUTS) {
          REPORT_DATA_OUTPUTS[state.selectedCode] = previousValue;
        }
        addReviewedCode(state.selectedCode);
        toast.error(
          'The paragraph reset did not save. Please try again.',
          { toastId: 'paragraph-reset-save-error' }
        );
      });

      setIsEditing(false);
      // Mark section as dirty since content was reset
      setSectionDirty(true);
      safeUpdatePosition(state.selectedCode);
    }
  };

  // Helper function to check if content has been modified from original
  const hasBeenModified = (code) => {
    if (!code) return false;
    return REPORT_DATA_OUTPUTS[code] !== initialContent[code];
  };

  const [showInitialWarning, setShowInitialWarning] = useState(true);
  // showWarning is provided by useApprovalTracking

  // State for initial content (on page load)
  const [initialContent, setInitialContent] = useState({});
  // Store initial content on page load, only for paragraphs with content
  useEffect(() => {
    if (REPORT_DATA_OUTPUTS) {
      const initialValues = {};
      // Loop through docStructure to get all codes
      Object.values(docStructure).forEach(section => {
        Object.values(section).forEach(({ code }) => {
          // Only store content that exists
          if (code && REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== "") {
            initialValues[code] = REPORT_DATA_OUTPUTS[code];
          }
        });
      });
      setInitialContent(initialValues);
    }
  }, [REPORT_DATA_OUTPUTS, docStructure]);

  // State for pre-edit content (before current edit session)
  const [preEditContent, setPreEditContent] = useState({});

  const documentContainerRef = useRef(null);
  const { panelRef, panelPosition, safeUpdatePosition } = useFloatingActionPanel({
    containerRef: documentContainerRef,
    selectedCode: state.selectedCode,
    isEditing,
    topOffset: -15,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [sectionDirty, setSectionDirty] = useState(false);

  const handleExitReview = async () => {
    setIsSaving(true);

    try {
      // Wait for any optimistic per-paragraph approval writes to commit so the
      // finalisation below reads the true, current approval state from Firestore.
      // PAD has the most variable paragraph set (weeks, health checks, lifestyle),
      // so racing an approval here is the highest-risk path for a missing-content
      // Patient Report — this barrier closes that race.
      await settlePendingApprovals();

      // Derive and persist the PAD approval flag transactionally from the fresh
      // snapshot. Only PAD's own approval fields are written here — cover page
      // approval metadata is owned exclusively by TriggerCoverPage's guarded
      // transaction to avoid contention between two back-to-back transactions.
      const { isFullyApproved: approvedNow, outstandingCodes, manageReports } = await finalizeSectionReview({
        patientId,
        modulePrefix: MODULE_PREFIX_CODE,
        moduleNumber: MODULE_NUMBER,
        docStructure,
      });

      // Reconcile the optimistic UI against the authoritative snapshot before
      // allowing the exit. Two situations must keep the clinician on the page:
      //   1. "Phantom approvals" — paragraphs shown green on screen whose
      //      background write actually failed (rapid clicking can overload the
      //      per-document transaction queue). Exiting now would leave the
      //      clinician believing the review is complete when it is not.
      //   2. The UI believed everything was approved but the snapshot found
      //      other outstanding content (e.g. paragraphs generated after this
      //      page loaded). The section flag has correctly been persisted as
      //      false, so the review genuinely is not finished.
      // In both cases we revert the affected paragraphs to needs-review,
      // re-fetch the patient document so the page reflects reality, and stay.
      const unpersisted = computeUnpersistedApprovals(reviewedCodes, outstandingCodes);
      if (!approvedNow && (isFullyApproved || unpersisted.length > 0)) {
        unpersisted.forEach(removeReviewedCode);
        await fetchPatientData(patientId);
        toast.error(
          unpersisted.length > 0
            ? `${unpersisted.length} paragraph approval${unpersisted.length === 1 ? ' did' : 's did'} not save. `
              + 'The affected paragraphs have been returned to "needs review" — please approve them again.'
            : 'Some paragraphs still need review before this section can be completed. '
              + 'The page has been refreshed to show them.',
          { toastId: 'review-exit-unsaved' }
        );
        return;
      }

      appendReportAuditEntry(patientId, {
        module: MODULE_PREFIX_CODE,
        eventType: 'approved',
        isFirstOccurrence: approvedNow && !manageReports?.padReportFirstApprovedAt,
      });

      // Only render section if AUTHORITATIVELY approved AND changed this session.
      const shouldRenderSection = approvedNow && sectionDirty;

      // Only trigger document generation if fully approved AND there were actual changes in this session
      if (shouldRenderSection) {
        TriggerRenderDocTemplate({
          reportStage: "Post-Assessment Discussion",
          clinicianId: auth.currentUser.uid,
          patientId: patientId
        });

        // TOC and SLaM Report Builder omit the standalone Cover Page (see
        // triggerPadCoverPageIfNeeded). Standard orgs keep the fire-and-forget
        // Cover Page trigger so the row on Manage Documents stays in sync.
        triggerPadCoverPageIfNeeded({
          replaceCARwithReportBuilder: wf.replaceCARwithReportBuilder,
          clinicianId: auth.currentUser.uid,
          patientId,
          triggerCoverPage: TriggerCoverPage,
        });
      }

      // Send the PAD completion email whenever all items are approved, regardless of whether
      // content changed this session. The backend has its own idempotency guard to prevent
      // duplicate sends, so this is safe to call on every exit where everything is approved.
      if (approvedNow) {
        TriggerPADCompletionEmail(patientId, auth.currentUser.uid);
      }

      // Kick off a background check to re-render the Patient Report if it's stale (run on every exit)
      // Fire-and-forget to keep UI fast
      rerenderPatientReportIfStale({
        patientId,
        clinicianId: auth.currentUser.uid,
        patientDataModel,
        organisation: claims?.organisation
      }).catch((e) => console.error('rerenderPatientReportIfStale (PAD) failed:', e));

      // Also re-render the Cover Page if stale (run on every exit)
      rerenderCoverPageIfStale({
        patientId,
        clinicianId: auth.currentUser.uid,
        patientDataModel,
        organisation: claims?.organisation
      }).catch((e) => console.error('rerenderCoverPageIfStale (PAD) failed:', e));

      // Clear any cached data before navigating
      clearPatientData();

      // Navigate back to documents page
      navigate(`/patient/${patientId}/documents`);
    } catch (error) {
      console.error('Error during document review completion:', error);
      // Surface the failure so the clinician knows the exit did not complete —
      // silently staying on the page looks like a frozen button.
      toast.error(
        'Could not save your review just now. Please check your connection and try again.',
        { toastId: 'review-exit-error' }
      );
    } finally {
      setIsSaving(false);
      // Reset dirty flag for next session
      setSectionDirty(false);
    }
  };

  const goToFirstUnreviewed = () => {
    // Only get paragraphs that are actually rendered on the page
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));

    if (firstUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = firstUnreviewed.getBoundingClientRect();

      // Calculate scroll position to center the paragraph
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      // Scroll to position
      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });

      // After scroll completes, trigger click handler
      setTimeout(() => {
        const nextCode = firstUnreviewed.id;
        const nextContent = REPORT_DATA_OUTPUTS[nextCode];

        // Trigger the full paragraph click handler
        handleParagraphClick(
          { currentTarget: firstUnreviewed },
          nextCode,
          nextContent
        );
      }, 300);
    }
  };

  useEffect(() => {
    // Apply the overflow: hidden style when the component mounts
    document.body.style.overflow = 'hidden';
    // Clean up the style when the component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, []);


  // Add useEffect to fetch model inputs
  useEffect(() => {
    const fetchModelInputs = async () => {
      try {
        const inputs = patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.inputs;
        if (inputs) {
          setState(prev => ({ ...prev, modelInputsFirestore: inputs }));
        } else {
          console.error('Inputs data is missing');
        }
      } catch (error) {
        console.error('Error fetching model inputs from Firestore:', error);
      }
    };

    if (patientDataModel) {
      fetchModelInputs();
    }
  }, [patientDataModel]);

  // Modify the overlay to explicitly allow scroll events
  useEffect(() => {
    // Store ref in a variable inside the effect
    const currentContainer = documentContainerRef.current;

    if (isEditing && currentContainer) {
      // Save original overflow setting
      const originalOverflow = currentContainer.style.overflow;

      // Ensure the document container is still scrollable during editing
      currentContainer.style.overflow = 'auto';

      // Create a wheel event handler that prevents propagation but allows default behavior
      const handleWheel = (e) => {
        e.stopPropagation();
        // But let the default scroll behavior happen
      };

      // Add the wheel event listener
      currentContainer.addEventListener('wheel', handleWheel, { passive: true });

      // Clean up function uses the stored variable
      return () => {
        currentContainer.removeEventListener('wheel', handleWheel);
        currentContainer.style.overflow = originalOverflow;
      };
    }
  }, [isEditing]);

  const handlePageClick = useCallback((event) => {
    // Ignore clicks if we're in edit mode
    if (isEditing) {
      return;
    }

    // Ignore clicks on paragraphs or the action panel
    if (
      event.target.closest('[data-paragraph-container]') ||
      event.target.closest('[data-action-panel]')
    ) {
      return;
    }
    setIsEditing(false);
    // Reset state
    setState(prev => ({
      ...prev,
      selectedCode: null,
      selectedContent: null,
      isSidePanelOpen: false
    }));
  }, [isEditing]);

  // Add useEffect to set up and clean up click handler
  useEffect(() => {
    // Store ref in a variable inside the effect
    const container = documentContainerRef.current;

    if (container) {
      container.addEventListener('click', handlePageClick);
    }

    return () => {
      // Use the stored variable in cleanup
      if (container) {
        container.removeEventListener('click', handlePageClick);
      }
    };
  }, [handlePageClick]);

  // Positioning handled by shared hook

  const handleParagraphApproval = async (code, isApproving) => {
    try {
      const docRef = doc(db, 'patients', patientId);

      // Base path to this section's approval bookkeeping. MODULE_NUMBER is a
      // numeric section id (e.g. "5"). Firestore's SDK backtick-escapes such
      // digit-leading segments automatically when it builds the update mask,
      // so the dotted-string key form is safe here.
      const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;

      // Write ONLY this paragraph's key rather than reading the whole document
      // and rewriting the entire approvedParagraphs map inside a
      // read-modify-write transaction.
      //
      // Why: runTransaction uses optimistic concurrency — every commit carries
      // a document-version precondition. When a clinician approves several
      // paragraphs in quick succession (especially on slow 3G/4G links), the
      // queued transactions all contend on the single patient document and most
      // fail with "the stored version does not match the required base version"
      // (failed-precondition). A plain updateDoc on a specific field path has no
      // version precondition, and Firestore merges concurrent writes to
      // distinct field paths server-side, so per-paragraph approvals no longer
      // fight over the document. (Paragraph codes are dot-free identifiers, and
      // the four field paths below are siblings — none is an ancestor of
      // another — so there is no conflicting-path rejection.)
      const updateData = {
        [`${sectionPath}.approvedParagraphs.${code}`]: isApproving
          ? {
              approvedAt: serverTimestamp(),
              approvedBy: auth.currentUser.uid,
              approvedByName: auth.currentUser.displayName,
              content: REPORT_DATA_OUTPUTS[code],
            }
          : deleteField(),
        [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
        [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
        [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
      };

      // Time the write so we can see (in the console) how quickly approvals
      // actually land in Firestore — updateDoc resolves on SERVER ack, so this
      // is the true round-trip, useful when stress-testing on 3G/Slow 4G. The
      // log is deliberately code-only (no patient content) to keep it PII-free.
      const startedAt = performance.now();
      await updateDoc(docRef, updateData);
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(
        `[approval] ${isApproving ? 'APPROVED' : 'UNAPPROVED'} "${code}" persisted to Firestore in ${elapsedMs}ms`
      );

      // Analytics mirror of the approval change that just persisted
      // (code only). Both directions share the review control that
      // wrote it, so both share this one call.
      emitParagraphApproval({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
        assessmentType: 'adhd',
        isApproving,
      });

      // Mark section as dirty due to approval change
      setSectionDirty(true);
    } catch (error) {
      console.error("Error updating paragraph approval:", error);
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      // Re-throw so the caller can observe the failure. Swallowing the error
      // here made failed approval writes invisible: the paragraph stayed green
      // on screen with no persisted approval behind it — the proven cause of
      // Patient Reports finalising with missing content.
      throw error;
    }
  };

  // Approved paragraphs are now loaded by useApprovalTracking

  const handleMarkAsDone = async () => {
    if (!state.selectedCode) {
      console.log('No code selected, cannot mark as done');
      return;
    }

    console.log('Starting handleMarkAsDone for code:', state.selectedCode);
    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      try {
        console.log('Calling handleParagraphApproval...');
        // Track the in-flight write so Save & Exit awaits it before finalising.
        trackApproval(
          handleParagraphApproval(state.selectedCode, true).catch(error => {
            console.error('Error in handleParagraphApproval:', error);
            // Roll back the optimistic update and tell the clinician — a
            // silently failed approval is exactly how incomplete sections
            // used to slip through to the Patient Report.
            removeReviewedCode(state.selectedCode);
            toast.error(
              'A paragraph approval did not save and has been returned to "needs review". Please approve it again.',
              { toastId: 'paragraph-approval-save-error' }
            );
          })
        );

        // Find next paragraph before updating UI state
        const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
        const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
        const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
          index > currentIndex && !reviewedCodes.has(p.id)
        );

        // Update reviewed state
        addReviewedCode(state.selectedCode);

        if (nextUnreviewed && documentContainerRef.current) {
          const containerRect = documentContainerRef.current.getBoundingClientRect();
          const nextRect = nextUnreviewed.getBoundingClientRect();

          const scrollTop = documentContainerRef.current.scrollTop +
            (nextRect.top - containerRect.top) -
            containerRect.height / 3;

          // Then scroll
          documentContainerRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });

          setTimeout(() => {
            const nextCode = nextUnreviewed.id;

            // Update state with new paragraph
            setState(prev => ({
              ...prev,
              selectedCode: nextCode,
              isSidePanelOpen: true,
              ...readParagraphInputs(state.modelInputsFirestore, nextCode),
              // informantInputs: state.modelInputsFirestore[nextCode]["Informant Prefill"],
            }));

            // Pass the new code directly to position update
            setTimeout(() => safeUpdatePosition(nextCode), 0);
          }, 300);
        } else {
          // This was the last paragraph - unselect it
          setState(prev => ({
            ...prev,
            selectedCode: null,
            isSidePanelOpen: false,
            clinicianInputs: null,
            clinicianDecisions: null,
            checkedExamples: null,
            article: null,
            // patientInputs: null,
            // informantInputs: null
          }));
        }
      } catch (error) {
        console.error('Error in handleMarkAsDone:', error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
      }
    }
  };

  // Update handleSkip with the same scroll logic
  const handleSkip = () => {
    if (!state.selectedCode) return;

    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      // Find next paragraph BEFORE updating UI state
      const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
      const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
      const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
        index > currentIndex && !reviewedCodes.has(p.id)
      );

      if (nextUnreviewed && documentContainerRef.current) {
        const containerRect = documentContainerRef.current.getBoundingClientRect();
        const nextRect = nextUnreviewed.getBoundingClientRect();

        const scrollTop = documentContainerRef.current.scrollTop +
          (nextRect.top - containerRect.top) -
          containerRect.height / 3;

        // Then scroll
        documentContainerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });

        setTimeout(() => {
          const nextCode = nextUnreviewed.id;

          // Update state with new paragraph
          setState(prev => ({
            ...prev,
            selectedCode: nextCode,
            isSidePanelOpen: true,
            ...readParagraphInputs(state.modelInputsFirestore, nextCode),
            // informantInputs: state.modelInputsFirestore[nextCode]["Informant Prefill"],
          }));

          // Pass the new code directly to position update
          setTimeout(() => safeUpdatePosition(nextCode), 0);
        }, 300);
      } else {
        // This was the last paragraph - unselect it
        setState(prev => ({
          ...prev,
          selectedCode: null,
          isSidePanelOpen: false,
          clinicianInputs: null,
          clinicianDecisions: null,
          checkedExamples: null,
          article: null,
          // patientInputs: null,
          // informantInputs: null
        }));
      }
    }
  };

  // Add progress calculation
  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  // Gate the entire interactive UI behind the feature flag. Without this,
  // the legacy doc structure is used during loading, which produces zero
  // matching paragraphs for v2 patients. That causes totalParagraphs === 0,
  // the header shows "Complete Review", and clicking it marks the report as
  // approved despite no paragraphs actually being reviewed.
  if (isLoadingFeatureFlag) {
    return (
      <Layout activePage="dashboard" scrollable={false}>
        <div className="h-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <svg className="animate-spin h-8 w-8 text-violet-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-slate-600 font-medium">Loading document...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <InitialWarningDialog showInitialWarning={showInitialWarning} setShowInitialWarning={setShowInitialWarning} />
      <div className="h-full flex flex-col overflow-hidden  ">

        {/* // Add overlay component when editing */}
        {isEditing && (
          <div
            className="fixed inset-0 bg-gray-900/20 z-10 transition-opacity duration-200"
            style={{
              pointerEvents: 'none' // Allow clicks to pass through to underlying elements
            }}
          />
        )}

               {/* Fixed header section */}
       <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${MODULE_TITLE}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
          />
          <WarningBar show={showWarning} />

          {/* Compare with Claude – dev/staging UAT only */}
          {isCompareAvailable && (
            <CompareModeToolbar
              isLoading={isCompareLoading}
              hasComparison={hasComparison}
              showClaude={showClaude}
              sideBySide={sideBySide}
              progress={compareProgress}
              onGenerate={triggerComparison}
              onShowGemini={() => setViewMode('gemini')}
              onShowClaude={() => setViewMode('claude')}
              onToggleSideBySide={toggleSideBySide}
            />
          )}
        </div>


        {/* Scrollable document section */}
        <div className="flex-1 px-8 pb-8 min-h-0 relative">
          <div className="document-display-container h-full overflow-hidden bg-slate-800">
            {/* Main flex container for document and side panel */}
            <div className="flex h-full">
              {/* Left side - Document container */}
              <div
                ref={documentContainerRef}
                className={`
                  w-1/2 relative m-4 p-16 border shadow-md bg-white
                  ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}
                `}
              >
                <div className="space-y-8 text-left">
                  {Object.entries(docStructure).map(([title, paragraphs]) => (
                    <Section
                      key={title}
                      title={title}
                      paragraphs={paragraphs}
                    />
                  ))}
                </div>
                <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />

                {/* Floating Action Panel */}
                <FloatingActionPanel
                  panelRef={panelRef}
                  position={panelPosition}
                  visible={Boolean(state.selectedCode)}
                  isEditing={isEditing}
                  hasBeenModified={hasBeenModified(state.selectedCode)}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveEdit}
                  onCancel={handleCancel}
                  onReset={handleReset}
                  onApprove={handleMarkAsDone}
                  onSkip={handleSkip}
                />
              </div>

              {sideBySide && hasComparison ? (
                <CompareClaudePanel
                  docStructure={docStructure}
                  claudeOutputs={claudeOutputs}
                  reportOutputs={REPORT_DATA_OUTPUTS}
                />
              ) : (
              <div className={`
                  w-2/5 p-4 flex flex-col
                  ${!state.selectedCode ? 'hidden' : ''}
                `}>
                <div className={`
                    flex-1 text-sm bg-gray-100 rounded-xl overflow-y-auto 
                    border shadow-sm
                    flex flex-col h-full
                  `}>
                  {state.selectedCode && (
                    <div className="flex flex-col h-full p-6 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex-none flex justify-between items-center">
                          <p className="text-xl font-bold text-gray-800 mb-2">AI Inputs</p>
                        </div>
                        <div className="overflow-y-auto">
                          {renderModelInput("Your decisions", state.clinicianDecisions)}
                          {renderModelInput("Your notes", state.clinicianInputs)}
                          {renderModelInput("Your checked examples", state.checkedExamples)}
                          {renderModelInput("Patient's report", state.patientInputs)}
                          {/* {renderModelInput("Informant comments", state.informantInputs)} */}
                          {hasArticleInput && renderModelInput(
                            "ThinkDivergent evidence-based article",
                            state.article
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}